import { createApi } from '@reduxjs/toolkit/query/react';
import baseQuery from 'store/app/entities/baseQuery';

const cloudMirrorApi = createApi({
  reducerPath: 'cloudMirror',
  baseQuery,
  tagTypes: ['CloudMirrors', 'CloudAccounts'],
  keepUnusedDataFor: 30,
  endpoints: build => ({
    listCloudMirrors: build.query({
      query: () => ({ url: 'v1/cloud-mirror' }),
      transformResponse: response => response.data,
      providesTags: result =>
        result
          ? [
              ...(result.mappings || []).map(({ _id: id }) => ({ type: 'CloudMirrors', id })),
              { type: 'CloudMirrors', id: 'LIST' },
            ]
          : [{ type: 'CloudMirrors', id: 'LIST' }],
    }),

    createCloudMirror: build.mutation({
      query: body => ({ url: 'v1/cloud-mirror', method: 'POST', body }),
      invalidatesTags: [{ type: 'CloudMirrors', id: 'LIST' }],
    }),

    syncCloudMirror: build.mutation({
      query: id => ({ url: `v1/cloud-mirror/${id}/sync`, method: 'POST' }),
      invalidatesTags: (result, error, id) => [
        { type: 'CloudMirrors', id },
        { type: 'SharedFiles', id: 'LIST-shared-' },
      ],
    }),

    pauseCloudMirror: build.mutation({
      query: ({ id, paused }) => ({
        url: `v1/cloud-mirror/${id}`,
        method: 'PATCH',
        body: { paused },
      }),
      invalidatesTags: (result, error, { id }) => [{ type: 'CloudMirrors', id }],
    }),

    deleteCloudMirror: build.mutation({
      query: id => ({ url: `v1/cloud-mirror/${id}`, method: 'DELETE' }),
      invalidatesTags: (result, error, id) => [
        { type: 'CloudMirrors', id: 'LIST' },
        { type: 'CloudMirrors', id },
        { type: 'SharedFiles', id: 'LIST-shared-' },
      ],
    }),

    listCloudAccounts: build.query({
      query: provider => ({ url: `v1/cloud-mirror/accounts?provider=${provider}` }),
      transformResponse: response => response.data,
      providesTags: (result, error, provider) => [{ type: 'CloudAccounts', id: provider }],
    }),

    deleteCloudAccount: build.mutation({
      // mode: 'keep' (snapshot folders as normal) | 'remove' (delete folders + files)
      query: ({ id, mode = 'keep' }) => ({
        url: `v1/cloud-mirror/accounts/${id}`,
        method: 'DELETE',
        body: { mode },
      }),
      invalidatesTags: () => [
        { type: 'CloudAccounts', id: 'google' },
        { type: 'CloudAccounts', id: 'microsoft' },
        { type: 'CloudMirrors', id: 'LIST' },
      ],
    }),

    reconnectCloudMirror: build.mutation({
      // Re-link a freshly authenticated token to its previously-owned folders.
      query: ({ tokenId }) => ({
        url: 'v1/cloud-mirror/reconnect',
        method: 'POST',
        body: { tokenId },
      }),
      invalidatesTags: () => [
        { type: 'CloudMirrors', id: 'LIST' },
        { type: 'SharedFiles', id: 'LIST-shared-' },
      ],
    }),

    getSharePointUrl: build.query({
      query: id => ({ url: `v1/cloud-mirror/accounts/${id}/sharepoint-url` }),
      transformResponse: response => response.data,
    }),
  }),
});

export const {
  useListCloudMirrorsQuery,
  useCreateCloudMirrorMutation,
  useSyncCloudMirrorMutation,
  usePauseCloudMirrorMutation,
  useDeleteCloudMirrorMutation,
  useListCloudAccountsQuery,
  useDeleteCloudAccountMutation,
  useReconnectCloudMirrorMutation,
  useLazyGetSharePointUrlQuery,
} = cloudMirrorApi;

export const { reducer: cloudMirrorReducer, middleware: cloudMirrorMiddleware } = cloudMirrorApi;
